import { Alert, Button, Drawer, Form, Input, InputNumber, Select } from 'antd';
import { useEffect } from 'react';
import { blankTyreForm, CURRENCIES, type TyreFormValues } from '../policies/tyre-form.policy';
import { useCanManageTyres } from '../hooks/use-can-manage-tyres';
import { SupplierSelect } from './SupplierSelect';
import { TyreBrandSelect } from './TyreBrandSelect';
import { TyreSizeSelect } from './TyreSizeSelect';

interface TyreFormDrawerProps {
  open: boolean;
  /** Present to edit that tyre's particulars; absent to register a new one. */
  editing?: { id: string; values: TyreFormValues; supplierName: string | null; brandName: string; sizeName: string };
  error: string | null;
  submitting: boolean;
  onSubmit: (values: TyreFormValues) => Promise<boolean>;
  onClose: () => void;
}

/** Register a tyre or edit its particulars. The cost is the purchase price only; fitting labour belongs on a work order. */
export function TyreFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: TyreFormDrawerProps) {
  const [form] = Form.useForm<TyreFormValues>();
  const canManage = useCanManageTyres();

  useEffect(() => {
    if (open) form.setFieldsValue(editing?.values ?? blankTyreForm());
  }, [open, editing, form]);

  async function submit(values: TyreFormValues) {
    if (await onSubmit(values)) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? `Edit ${editing.values.serial}` : 'Register a tyre'} placement="right" size={420} destroyOnHidden>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="serial" label="Serial number" rules={[{ required: true, message: 'Enter the serial number' }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="brandId" label="Brand" rules={[{ required: true, message: 'Choose the brand' }]}>
          <TyreBrandSelect savedName={editing?.brandName} canAdd={canManage} />
        </Form.Item>
        <Form.Item name="sizeId" label="Size" rules={[{ required: true, message: 'Choose the size' }]}>
          <TyreSizeSelect savedName={editing?.sizeName} canAdd={canManage} />
        </Form.Item>
        <Form.Item name="pattern" label="Pattern" rules={[{ required: true, message: 'Enter the tread pattern' }]}>
          <Input />
        </Form.Item>
        <div className="grid grid-cols-[1fr_6rem] gap-3">
          <Form.Item
            name="cost"
            label="Purchase price"
            extra="The tyre only, not fitting labour."
            rules={[{ required: true, type: 'number', min: 0.01, message: 'Enter the price' }]}
          >
            <InputNumber className="w-full" min={0} step={100} precision={2} />
          </Form.Item>
          <Form.Item name="currency" label="Currency">
            <Select options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
          </Form.Item>
        </div>
        <Form.Item name="supplierId" label="Supplier">
          <SupplierSelect savedName={editing?.supplierName} />
        </Form.Item>
        <Form.Item name="purchasedOn" label="Purchase date" rules={[{ required: true, message: 'Enter the purchase date' }]}>
          <Input type="date" />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Register tyre'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
